import React, { useEffect, useMemo, useState } from 'react';
import { HostCodesAPI } from '../api/HostCodeEndpoints';
import HostCodeCreation from '../components/hostcode-management/HostCodeCreation';
import ConfirmDeleteModal from './dashboard/components/ConfirmDeleteModal';
import { toLocalDateTimeInput } from '../utils/localDate';

// ⬇️ eigenes Styling
import './dashboard/components/Marker-UI.css';
import './dashboard/Dashboard-UI.css';
import '../components/hostcode-management/HostCode-UI.css';
import PageTitle from '../components/PageTitle';

const STATUS_LABEL = { live: 'Live', pending: 'Offen', past: 'Vergangen' };

/** "Sa 31.10.2026" from 'YYYY-MM-DD', read as a local day. */
const formatDay = (value) => {
  if (!value) return '–';
  const [y, m, d] = String(value).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('de-AT', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
};

/** A code with its secret end hidden, for places that only need to recognise it. */
const maskCode = (code) => `${String(code).slice(0, -5)}•••••`;

const normalizeAccount = (value) => String(value || '').trim().replace(/^@+/, '').toLowerCase();

function Chevron({ open }) {
  return (
    <svg className={`hco-chevron${open ? ' hco-chevron--open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" aria-hidden="true">
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function CodeCell({ code, revealed, copied, onReveal, onCopy }) {
  return (
    <span className="hc-code-wrap">
      <span>{code.slice(0, -5)}</span>
      <span className={revealed ? '' : 'hc-code-blur'}>{code.slice(-5)}</span>
      <button
        className="hc-reveal-btn"
        onClick={(e) => { e.stopPropagation(); onReveal(); }}
        title={revealed ? 'Verbergen' : 'Anzeigen'}
        aria-label={revealed ? 'Verbergen' : 'Anzeigen'}
      >
        {revealed ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="13" height="13" aria-hidden="true">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
            <line x1="1" y1="1" x2="23" y2="23"/>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="13" height="13" aria-hidden="true">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
        )}
      </button>
      <button
        className={`hc-reveal-btn${copied ? ' hc-reveal-btn--copied' : ''}`}
        onClick={(e) => { e.stopPropagation(); onCopy(); }}
        title="Kopieren"
        aria-label="Code kopieren"
      >
        {copied ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13" aria-hidden="true">
            <path d="M20 6L9 17l-5-5"/>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="13" height="13" aria-hidden="true">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
          </svg>
        )}
      </button>
    </span>
  );
}

/** The events of one code, loaded when its row is opened. */
function CodeEvents({ hostCode }) {
  const [events, setEvents] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    HostCodesAPI.getEvents(hostCode.code)
      .then((data) => { if (active) setEvents(Array.isArray(data) ? data : []); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [hostCode.code]);

  if (failed) return <div className="hco-panel-note">Events konnten nicht geladen werden.</div>;
  if (events == null) {
    return (
      <div className="hco-events" aria-busy="true" aria-label="Lädt Events">
        {[0, 1, 2].map((key) => <div key={key} className="hco-event hco-event--skeleton" />)}
      </div>
    );
  }
  if (events.length === 0) return <div className="hco-panel-note">Unter diesem Code gibt es keine Events.</div>;

  const own = normalizeAccount(hostCode.instagramName);
  return (
    <div className="hco-events">
      {events.map((event) => {
        const account = normalizeAccount(event.instagramName);
        return (
          <div key={event.id} className={`hco-event${event.status === 'past' ? ' hco-event--past' : ''}`}>
            <span className="hco-event-date">{formatDay(event.eventDate)}</span>
            <span className="hco-event-title" title={event.title}>{event.title || 'Ohne Titel'}</span>
            <span className="hco-event-meta">
              {account && account !== own && <span className="hco-chip">@{account}</span>}
              {event.movedFrom && (
                <span className="hco-chip hco-chip--moved" title={`Von ${maskCode(event.movedFrom)} zu seinem Veranstalter verschoben`}>
                  verschoben von {maskCode(event.movedFrom)}
                </span>
              )}
              <span className={`hco-status hco-status--${event.status}`}>{STATUS_LABEL[event.status] || event.status}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

const FILTERS = {
  all: () => true,
  linked: (hc) => !!hc.instagramName,
  unlinked: (hc) => !hc.instagramName,
  repaired: (hc) => hc.boundByRepair || hc.movedAway > 0,
};

export default function HostCodeManagement() {
  const [hostCodes, setHostCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingCode, setEditingCode] = useState(null);
  const [editData, setEditData] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, code: null, title: '' });
  const [revealedCodes, setRevealedCodes] = useState(new Set());
  const [copiedCode, setCopiedCode] = useState(null);
  const [openCode, setOpenCode] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [withEventsOnly, setWithEventsOnly] = useState(false);

  const toggleReveal = (code) => {
    setRevealedCodes((prev) => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  };

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 1500);
  };

  const fetchHostCodes = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await HostCodesAPI.getOverview();
      setHostCodes(data || []);
    } catch (err) {
      console.error('Fehler beim Laden der Host-Codes:', err);
      setError('Konnte Host-Codes nicht laden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHostCodes();
  }, []);

  const stats = useMemo(() => ({
    total: hostCodes.length,
    withHosts: hostCodes.filter((hc) => hc.hosts > 0).length,
    linked: hostCodes.filter(FILTERS.linked).length,
    unlinked: hostCodes.filter(FILTERS.unlinked).length,
    unlinkedWithEvents: hostCodes.filter((hc) => !hc.instagramName && hc.events > 0).length,
    repaired: hostCodes.filter(FILTERS.repaired).length,
    moved: hostCodes.reduce((sum, hc) => sum + (hc.movedAway || 0), 0),
  }), [hostCodes]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase().replace(/^@/, '');
    return hostCodes
      .filter(FILTERS[filter])
      .filter((hc) => !withEventsOnly || hc.events > 0)
      .filter((hc) => !q || hc.code.toLowerCase().includes(q) || String(hc.instagramName || '').toLowerCase().includes(q));
  }, [hostCodes, filter, search, withEventsOnly]);

  const handleEditClick = (hc) => {
    setEditingCode(hc.code);
    setEditData({
      instagramName: hc.instagramName || '',
      amountRedeemable: hc.amountRedeemable,
      expiresAt: hc.expiresAt
    });
  };

  const handleSave = async () => {
    try {
      await HostCodesAPI.updateHostCode(editingCode, {
        name: editingCode,
        // Empty takes the account off the code; its hosts then type it themselves again.
        instagramName: editData.instagramName.trim(),
        amountRedeemable: editData.amountRedeemable,
        expiresAt: editData.expiresAt
      });
      setEditingCode(null);
      fetchHostCodes();
    } catch (err) {
      console.error('Fehler beim Aktualisieren des Host-Codes:', err);
      alert(err.response?.status === 400
        ? 'Ungültiger Instagram-Account (z. B. @flucc oder der Profil-Link).'
        : 'Update fehlgeschlagen.');
    }
  };

  const handleDelete = (code) => {
    setConfirmDelete({ open: true, code, title: code });
  };

  const handleDeleteConfirm = async () => {
    const { code } = confirmDelete;
    setConfirmDelete({ open: false, code: null, title: '' });
    try {
      await HostCodesAPI.deleteHostCode(code);
      fetchHostCodes();
    } catch (err) {
      console.error('Fehler beim Löschen des Host-Codes:', err);
      alert('Löschen fehlgeschlagen.');
    }
  };

  const handleDeleteCancel = () => setConfirmDelete({ open: false, code: null, title: '' });

  const statButton = (key, label, value, sub, accent) => (
    <button type="button" className={`db-stat ${accent}${filter === key ? ' db-stat--active' : ''}`}
      onClick={() => setFilter(filter === key && key !== 'all' ? 'all' : key)}>
      <span className="db-stat-label">{label}</span>
      <span className="db-stat-value">{loading ? '–' : value}</span>
      <span className="db-stat-sub">{loading ? '' : sub}</span>
    </button>
  );

  const now = Date.now();

  return (
    <div className="hc-page">
      <div className="page-header">
        <PageTitle title="Host-Zugänge" description="Codes, mit denen Veranstalter ihre Events selbst in Schick eintragen. Jeder Code gehört zu einem Instagram-Account." />
      </div>

      {/* Hinzufügen */}
      <div className="hc-toolbar">
        <button className="btn-add btn-add--green" onClick={() => setShowCreate(true)}>
          <svg
            className="btn-add-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          Host-Code
        </button>
      </div>

      {/* Modals */}
      {showCreate && (
        <HostCodeCreation
          onCreated={fetchHostCodes}
          onClose={() => setShowCreate(false)}
        />
      )}

      <ConfirmDeleteModal
        open={confirmDelete.open}
        markerTitle={confirmDelete.title}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      <div className="db-stats">
        {statButton('all', 'Host-Codes', stats.total, `${stats.withHosts} mit eingelöstem Host`, '')}
        {statButton('linked', 'Mit Instagram', stats.linked, 'Events laufen fest unter dem Account', 'hco-stat--linked')}
        {statButton('unlinked', 'Ohne Instagram', stats.unlinked,
          stats.unlinkedWithEvents > 0 ? `${stats.unlinkedWithEvents} davon mit Events` : 'Hosts tippen den Account selbst', 'hco-stat--unlinked')}
        {statButton('repaired', 'Durch Korrektur', stats.repaired,
          stats.moved > 0 ? `${stats.moved} Events zum Veranstalter verschoben` : 'Codes, die die Korrektur geändert hat', 'hco-stat--repaired')}
      </div>

      <div className="db-toolbar">
        <label className="db-search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" aria-hidden="true">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
          </svg>
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Code oder @account…" aria-label="Host-Codes durchsuchen" />
        </label>
        <label className="db-toggle">
          <input type="checkbox" checked={withEventsOnly} onChange={(e) => setWithEventsOnly(e.target.checked)} />
          Nur mit Events
        </label>
        <span className="db-spacer" />
        {!loading && <span className="db-result">{rows.length} {rows.length === 1 ? 'Code' : 'Codes'}</span>}
      </div>

      {error && <p className="hc-error">{error}</p>}

      {loading && hostCodes.length === 0 ? (
        <div className="db-skeleton" aria-busy="true" aria-label="Lädt Host-Codes">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="db-skeleton-row" />)}
        </div>
      ) : rows.length === 0 && !error ? (
        <div className="db-empty">
          <strong>{hostCodes.length === 0 ? 'Noch keine Host-Codes' : 'Keine Treffer'}</strong>
          <span>{hostCodes.length === 0 ? 'Neue Codes erscheinen hier, sobald sie erstellt sind.' : 'Andere Suche oder Filter probieren.'}</span>
        </div>
      ) : rows.length > 0 && (
        <div className="hc-card">
          <table className="hc-table hco-table">
            <thead>
              <tr>
                <th aria-label="Details" />
                <th>Code</th>
                <th>Instagram</th>
                <th>Events</th>
                <th>Einlösungen</th>
                <th>Gültig bis</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((hc) => {
                const editing = editingCode === hc.code;
                const open = openCode === hc.code;
                const expired = hc.expiresAt && new Date(hc.expiresAt).getTime() < now;
                return (
                  <React.Fragment key={hc.code}>
                    <tr className={`hco-row${open ? ' hco-row--open' : ''}`} onClick={() => !editing && setOpenCode(open ? null : hc.code)}>
                      <td className="hco-toggle-cell">
                        <button type="button" className="hco-toggle" aria-expanded={open}
                          aria-label={open ? 'Events zuklappen' : 'Events aufklappen'}
                          onClick={(e) => { e.stopPropagation(); setOpenCode(open ? null : hc.code); }}>
                          <Chevron open={open} />
                        </button>
                      </td>

                      <td className="mono">
                        <CodeCell
                          code={hc.code}
                          revealed={revealedCodes.has(hc.code)}
                          copied={copiedCode === hc.code}
                          onReveal={() => toggleReveal(hc.code)}
                          onCopy={() => handleCopy(hc.code)}
                        />
                      </td>

                      <td onClick={(e) => editing && e.stopPropagation()}>
                        {editing ? (
                          <input
                            className="hc-input"
                            type="text"
                            value={editData.instagramName}
                            placeholder="@account"
                            autoCapitalize="none"
                            spellCheck={false}
                            onChange={(e) => setEditData({ ...editData, instagramName: e.target.value })}
                          />
                        ) : hc.instagramName ? (
                          <span className="hco-ig">
                            <a href={`https://www.instagram.com/${hc.instagramName}/`} target="_blank" rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}>@{hc.instagramName}</a>
                            {hc.boundByRepair && <span className="hco-chip hco-chip--repair" title="Von der Korrektur aus den Events des Codes gesetzt">Korrektur</span>}
                          </span>
                        ) : (
                          <span className="hc-muted">nicht verknüpft</span>
                        )}
                      </td>

                      <td>
                        <span className="hco-count">{hc.events}</span>
                        <span className="hco-meta">
                          {[
                            hc.upcoming > 0 ? `${hc.upcoming} kommend` : null,
                            hc.pending > 0 ? `${hc.pending} offen` : null,
                            hc.movedAway > 0 ? `${hc.movedAway} verschoben` : null,
                          ].filter(Boolean).join(' · ')}
                        </span>
                      </td>

                      <td onClick={(e) => editing && e.stopPropagation()}>
                        {editing ? (
                          <input
                            className="hc-input"
                            type="number"
                            min={0}
                            value={editData.amountRedeemable ?? 0}
                            onChange={(e) =>
                              setEditData({ ...editData, amountRedeemable: Number(e.target.value) })
                            }
                          />
                        ) : (
                          <>
                            <span className="hco-count hco-count--small">{hc.redeemedCount} / {hc.amountRedeemable}</span>
                            <span className="hco-meta">{hc.hosts === 1 ? '1 Host' : `${hc.hosts} Hosts`}</span>
                          </>
                        )}
                      </td>

                      <td onClick={(e) => editing && e.stopPropagation()}>
                        {editing ? (
                          <input
                            className="hc-input"
                            type="datetime-local"
                            value={toLocalDateTimeInput(editData.expiresAt)}
                            onChange={(e) =>
                              setEditData({ ...editData, expiresAt: new Date(e.target.value).toISOString() })
                            }
                          />
                        ) : (
                          <span className={expired ? 'hco-expired' : ''}>
                            {new Date(hc.expiresAt).toLocaleDateString('de-AT')}
                            {expired && <span className="hco-meta">abgelaufen</span>}
                          </span>
                        )}
                      </td>

                      <td className="hc-actions" onClick={(e) => e.stopPropagation()}>
                        {editing ? (
                          <>
                            <button className="hc-btn hc-btn-primary" onClick={handleSave}>
                              Speichern
                            </button>
                            <button className="hc-btn hc-btn-ghost" onClick={() => setEditingCode(null)}>
                              Abbrechen
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="hc-btn" onClick={() => handleEditClick(hc)}>
                              Bearbeiten
                            </button>
                            <button className="hc-btn hc-btn-danger" onClick={() => handleDelete(hc.code)}>
                              Löschen
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                    {open && (
                      <tr className="hco-panel-row">
                        <td colSpan={7}>
                          <CodeEvents hostCode={hc} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
